import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { manualEventTemplates } from "../../../mocks/admin";
import { useAuth } from "../../../contexts/AuthContext";

interface WorkspaceRow {
  id: string;
  name: string;
  owner_id: string;
  play_mode: string;
  company_type: string;
}

interface MemberRow {
  userId: string;
  displayName: string;
  email: string;
  jobRole: string;
}

interface Notification {
  msg: string;
  type: "success" | "danger";
}

interface HistoryRow {
  id: string;
  time: string;
  date: string;
  event: string;
  eventType: string;
  targetName: string;
  toRole: string;
  capDelta: number;
  repDelta: number;
}

const JOB_ROLE_LABELS: Record<string, string> = {
  CEO: "CEO / 대표이사", AR: "A&R 담당", Finance: "재무 담당",
  Marketing: "마케팅 담당", Legal: "법무 담당",
  PD: "공연기획 PD", Ticketing: "티켓영업 MD",
  Projects: "사업팀장", Fundraising: "후원개발팀", Evaluation: "사업평가위원",
  Editor: "에디터", Contract: "계약 MD",
  Creative: "크리에이티브 디렉터", AE: "영업 AE",
  Production: "영상 PD", Data: "데이터 분석가",
};

export default function EventDispatcher() {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [loadingWs, setLoadingWs] = useState(true);
  const [selectedWs, setSelectedWs] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [notification, setNotification] = useState<Notification | null>(null);
  const [dispatching, setDispatching] = useState(false);

  const [targetMode, setTargetMode] = useState<"all" | "individual">("all");
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMember, setSelectedMember] = useState<string>("");

  const [dispatchHistory, setDispatchHistory] = useState<HistoryRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyView, setHistoryView] = useState<"timeline" | "stats">("timeline");

  const loadWorkspaces = useCallback(async () => {
    setLoadingWs(true);
    const { data, error } = await supabase
      .from("workspaces")
      .select("id, name, owner_id, play_mode, company_type")
      .order("created_at", { ascending: false });
    if (!error && data) setWorkspaces(data as WorkspaceRow[]);
    setLoadingWs(false);
  }, []);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  // DB에서 발송 이력 로드 (role_notifications 테이블 활용)
  const loadHistory = useCallback(async (wsId: string) => {
    if (!wsId) { setDispatchHistory([]); return; }
    setLoadingHistory(true);
    const { data } = await supabase
      .from("role_notifications")
      .select("id, title, body, to_role, to_user_id, created_at")
      .eq("workspace_id", wsId)
      .eq("type", "admin_event")
      .order("created_at", { ascending: false })
      .limit(30);

    const rows: HistoryRow[] = (data ?? []).map((row) => {
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(row.body as string); } catch { /* ignore */ }
      const dt = new Date(row.created_at as string);
      return {
        id: row.id as string,
        time: dt.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }),
        date: dt.toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" }),
        event: (parsed.headline as string) ?? (row.title as string) ?? "-",
        eventType: (parsed.eventType as string) ?? "opportunity",
        targetName: (parsed.targetName as string) ?? (row.to_role === "ALL" ? "전체 학생" : "특정 학생"),
        toRole: (row.to_role as string) ?? "ALL",
        capDelta: (parsed.capDelta as number) ?? 0,
        repDelta: (parsed.repDelta as number) ?? 0,
      };
    });
    setDispatchHistory(rows);
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    if (selectedWs) loadHistory(selectedWs);
    else setDispatchHistory([]);
  }, [selectedWs, loadHistory]);

  const loadMembers = useCallback(async (wsId: string) => {
    if (!wsId) { setMembers([]); return; }
    setLoadingMembers(true);
    setSelectedMember("");

    const { data: wm } = await supabase
      .from("workspace_members")
      .select("user_id, job_role")
      .eq("workspace_id", wsId);

    if (!wm || wm.length === 0) { setMembers([]); setLoadingMembers(false); return; }

    const ids = wm.map(m => m.user_id);
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, display_name, email")
      .in("id", ids);

    const profMap: Record<string, { display_name: string; email: string }> = {};
    (profiles ?? []).forEach(p => { profMap[p.id] = p; });

    const rows: MemberRow[] = wm.map(m => ({
      userId: m.user_id,
      displayName: profMap[m.user_id]?.display_name ?? "익명",
      email: profMap[m.user_id]?.email ?? "-",
      jobRole: m.job_role ?? "-",
    }));

    setMembers(rows);
    setLoadingMembers(false);
  }, []);

  useEffect(() => {
    if (selectedWs) loadMembers(selectedWs);
    else setMembers([]);
  }, [selectedWs, loadMembers]);

  const handleDispatch = async () => {
    if (!selectedWs || !selectedEvent || !user) return;
    if (targetMode === "individual" && !selectedMember) return;

    const evt = manualEventTemplates.find((e) => e.id === selectedEvent);
    const ws = workspaces.find((w) => w.id === selectedWs);
    if (!evt || !ws) return;

    setDispatching(true);

    const targetMember = members.find(m => m.userId === selectedMember);
    const targetName = targetMode === "all" ? "전체 학생" : (targetMember?.displayName ?? "알 수 없음");

    const bodyJson = JSON.stringify({
      headline: evt.label,
      description: evt.description,
      capDelta: evt.capDelta,
      repDelta: evt.repDelta,
      eventType: evt.type,
      icon: evt.icon,
      dispatchedAt: new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }),
      targetMode,
      targetName,
    });

    const insertPayload: Record<string, unknown> = {
      workspace_id: selectedWs,
      from_user_id: user.id,
      to_role: targetMode === "all" ? "ALL" : "INDIVIDUAL",
      type: "admin_event",
      title: `[강사 이벤트] ${evt.label}`,
      body: bodyJson,
      ref_id: evt.id,
      is_read: false,
    };

    if (targetMode === "individual" && selectedMember) {
      insertPayload.to_user_id = selectedMember;
    }

    const { error } = await supabase.from("role_notifications").insert(insertPayload);

    if (!error && (evt.capDelta !== 0 || evt.repDelta !== 0)) {
      if (targetMode === "individual" && selectedMember) {
        const { data: cs } = await supabase
          .from("company_states")
          .select("capital, reputation")
          .eq("workspace_id", selectedWs)
          .eq("user_id", selectedMember)
          .maybeSingle();
        if (cs) {
          await supabase.from("company_states").update({
            capital: Math.max(0, cs.capital + evt.capDelta),
            reputation: Math.max(0, Math.min(100, cs.reputation + evt.repDelta)),
          }).eq("workspace_id", selectedWs).eq("user_id", selectedMember);
        }
      } else if (targetMode === "all") {
        const { data: allStates } = await supabase
          .from("company_states")
          .select("user_id, capital, reputation")
          .eq("workspace_id", selectedWs);
        if (allStates && allStates.length > 0) {
          await Promise.all(allStates.map(s =>
            supabase.from("company_states").update({
              capital: Math.max(0, s.capital + evt.capDelta),
              reputation: Math.max(0, Math.min(100, s.reputation + evt.repDelta)),
            }).eq("workspace_id", selectedWs).eq("user_id", s.user_id)
          ));
        }
      }
    }

    setDispatching(false);

    if (!error) {
      const effectMsg = evt.capDelta !== 0 || evt.repDelta !== 0
        ? ` (자본금 ${evt.capDelta > 0 ? "+" : ""}${evt.capDelta.toLocaleString()}만, 직급점수 ${evt.repDelta > 0 ? "+" : ""}${evt.repDelta}pt 즉시 반영)`
        : "";
      setNotification({
        msg: `[${ws.name}] '${evt.label}' → ${targetName}에게 발송 완료${effectMsg}`,
        type: evt.type === "opportunity" ? "success" : "danger",
      });
      setSelectedEvent("");
      setSelectedMember("");
      // 이력 새로고침
      await loadHistory(selectedWs);
    } else {
      setNotification({ msg: `발송 실패: ${error.message}`, type: "danger" });
    }
    setTimeout(() => setNotification(null), 4500);
  };

  const canDispatch = selectedWs && selectedEvent && !dispatching && !loadingWs
    && (targetMode === "all" || (targetMode === "individual" && selectedMember));

  // 통계 계산
  const statsData = {
    total: dispatchHistory.length,
    opportunity: dispatchHistory.filter(h => h.eventType === "opportunity").length,
    crisis: dispatchHistory.filter(h => h.eventType !== "opportunity").length,
    individual: dispatchHistory.filter(h => h.toRole === "INDIVIDUAL").length,
    allBroadcast: dispatchHistory.filter(h => h.toRole === "ALL").length,
  };

  return (
    <div className="grid grid-cols-5 gap-5">
      {/* 왼쪽: 이벤트 발송 폼 */}
      <div className="col-span-3 space-y-5">
        {notification && (
          <div className={`px-4 py-3 rounded-xl text-sm flex items-center gap-2 border ${
            notification.type === "success"
              ? "bg-[#4B6CF7]/10 text-[#7A9BFF] border-[#4B6CF7]/20"
              : "bg-white/[0.04] text-[#CCCCCC] border-white/[0.08]"
          }`}>
            <i className={`${notification.type === "success" ? "ri-send-plane-fill" : "ri-alarm-warning-fill"} text-lg`}></i>
            {notification.msg}
          </div>
        )}

        <div className="bg-[#111112] rounded-xl border border-white/[0.06] p-5">
          <h3 className="text-[#F2F2F2] font-semibold text-sm mb-4 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-remote-control-line text-[#7A9BFF] text-base"></i>
            </div>
            이벤트 수동 발송
            <span className="text-[10px] text-[#4B6CF7]/60 border border-[#4B6CF7]/20 px-1.5 py-0.5 rounded font-normal ml-1">
              Supabase Realtime
            </span>
          </h3>

          <div className="space-y-4">
            {/* 1. 워크스페이스 선택 */}
            <div>
              <label className="text-[#555555] text-xs mb-1.5 block">
                대상 워크스페이스
                <span className="text-[#3D3D3D] ml-1">({loadingWs ? "로딩 중..." : `${workspaces.length}개`})</span>
              </label>
              {loadingWs ? (
                <div className="w-full h-10 bg-white/[0.02] border border-white/[0.06] rounded-lg animate-pulse" />
              ) : workspaces.length === 0 ? (
                <div className="text-[#3D3D3D] text-xs py-2">
                  등록된 워크스페이스가 없습니다.
                  <button type="button" onClick={loadWorkspaces} className="ml-2 text-[#555555] hover:text-[#888888] cursor-pointer whitespace-nowrap">새로고침</button>
                </div>
              ) : (
                <select
                  value={selectedWs}
                  onChange={(e) => setSelectedWs(e.target.value)}
                  className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm rounded-lg px-3 py-2.5 border border-white/[0.08] focus:outline-none focus:border-[#4B6CF7]/30 cursor-pointer"
                >
                  <option value="">워크스페이스를 선택하세요...</option>
                  {workspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>{ws.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* 2. 발송 대상 */}
            {selectedWs && (
              <div>
                <label className="text-[#555555] text-xs mb-1.5 block">발송 대상</label>
                <div className="flex gap-2 mb-3">
                  {(["all", "individual"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setTargetMode(m); setSelectedMember(""); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        targetMode === m
                          ? "border-[#4B6CF7]/40 bg-[#4B6CF7]/10 text-[#7A9BFF]"
                          : "border-white/[0.06] text-[#555555] hover:border-white/10 hover:text-[#888888]"
                      }`}
                    >
                      <i className={`${m === "all" ? "ri-group-line" : "ri-user-line"} text-sm`}></i>
                      {m === "all" ? "전체 발송" : "특정 학생"}
                    </button>
                  ))}
                </div>

                {targetMode === "individual" && (
                  <div>
                    {loadingMembers ? (
                      <div className="w-full h-10 bg-white/[0.02] border border-white/[0.06] rounded-lg animate-pulse" />
                    ) : members.length === 0 ? (
                      <div className="text-[#3D3D3D] text-xs py-2 px-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
                        이 워크스페이스에 입장한 학생이 없어요
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-1.5 max-h-40 overflow-y-auto pr-1">
                        {members.map((m) => (
                          <button
                            key={m.userId}
                            type="button"
                            onClick={() => setSelectedMember(m.userId)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                              selectedMember === m.userId
                                ? "border-[#E8375F]/40 bg-[#E8375F]/[0.08]"
                                : "border-white/[0.05] hover:border-white/10 bg-white/[0.01]"
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              selectedMember === m.userId
                                ? "bg-[#E8375F]/20 text-[#E8375F]"
                                : "bg-white/[0.06] text-[#555555]"
                            }`}>
                              {m.displayName[0]?.toUpperCase() ?? "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium truncate ${selectedMember === m.userId ? "text-[#CCCCCC]" : "text-[#888888]"}`}>
                                {m.displayName}
                              </div>
                              <div className="text-[10px] text-[#3D3D3D] truncate">
                                {JOB_ROLE_LABELS[m.jobRole] ?? m.jobRole} · {m.email}
                              </div>
                            </div>
                            {selectedMember === m.userId && (
                              <i className="ri-checkbox-circle-fill text-[#E8375F] text-sm shrink-0"></i>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 3. 이벤트 선택 */}
            <div>
              <label className="text-[#555555] text-xs mb-1.5 block">발송할 이벤트</label>
              <div className="grid grid-cols-2 gap-2">
                {manualEventTemplates.map((evt) => (
                  <button
                    key={evt.id}
                    type="button"
                    onClick={() => setSelectedEvent(evt.id)}
                    className={`flex items-start gap-2.5 p-3 rounded-lg border text-left transition-all cursor-pointer ${
                      selectedEvent === evt.id
                        ? evt.type === "opportunity"
                          ? "border-[#4B6CF7]/40 bg-[#4B6CF7]/[0.07]"
                          : "border-[#E8375F]/30 bg-[#E8375F]/[0.05]"
                        : "border-white/[0.06] hover:border-white/10 bg-[#0C0C0E]"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className={`${evt.icon} text-sm ${evt.type === "opportunity" ? "text-[#7A9BFF]" : "text-[#888888]"}`}></i>
                    </div>
                    <div>
                      <div className="text-[#CCCCCC] text-xs font-medium">{evt.label}</div>
                      <div className="text-[#3D3D3D] text-[10px] mt-0.5 leading-relaxed">{evt.description}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        {evt.capDelta !== 0 && (
                          <span className={`text-[9px] tabular-nums ${evt.capDelta > 0 ? "text-[#7A9BFF]" : "text-[#666666]"}`}>
                            자본금 {evt.capDelta > 0 ? "+" : ""}{evt.capDelta.toLocaleString()}만
                          </span>
                        )}
                        {evt.repDelta !== 0 && (
                          <span className={`text-[9px] tabular-nums ${evt.repDelta > 0 ? "text-[#CCCCCC]/60" : "text-[#555555]"}`}>
                            명성 {evt.repDelta > 0 ? "+" : ""}{evt.repDelta}pt
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 미리보기 */}
            {selectedWs && selectedEvent && (
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg px-4 py-3 text-xs space-y-1">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-wider mb-1">발송 미리보기</div>
                <div className="flex items-center gap-2">
                  <i className="ri-building-line text-[#555555] text-xs"></i>
                  <span className="text-[#555555]">{workspaces.find(w => w.id === selectedWs)?.name ?? "-"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-user-line text-[#555555] text-xs"></i>
                  <span className="text-[#555555]">
                    {targetMode === "all"
                      ? `전체 학생 (${members.length}명)`
                      : members.find(m => m.userId === selectedMember)?.displayName ?? "학생 선택 안 됨"
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-notification-line text-[#555555] text-xs"></i>
                  <span className="text-[#555555]">{manualEventTemplates.find(e => e.id === selectedEvent)?.label ?? "-"}</span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleDispatch}
              disabled={!canDispatch}
              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${
                canDispatch
                  ? "bg-[#4B6CF7] text-white cursor-pointer hover:bg-[#5B7CF7]"
                  : "bg-white/[0.04] text-[#2A2A2A] cursor-not-allowed"
              }`}
            >
              {dispatching
                ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>발송 중...</span>
                : <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-line"></i>이벤트 발송 (Realtime)</span>
              }
            </button>
          </div>
        </div>
      </div>

      {/* 오른쪽: 발송 이력 타임라인 */}
      <div className="col-span-2 space-y-4">
        <div className="bg-[#111112] rounded-xl border border-white/[0.06] overflow-hidden" style={{ minHeight: 560 }}>
          {/* 헤더 */}
          <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-history-line text-[#555555] text-sm"></i>
              <span className="text-[#888888] text-xs font-semibold">발송 이력</span>
              {dispatchHistory.length > 0 && (
                <span className="text-[9px] bg-[#4B6CF7]/20 text-[#7A9BFF] px-1.5 py-0.5 rounded-full font-semibold">{dispatchHistory.length}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {(["timeline", "stats"] as const).map(v => (
                <button key={v} type="button" onClick={() => setHistoryView(v)}
                  className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    historyView === v ? "bg-white/[0.08] text-[#CCCCCC]" : "text-[#444444] hover:text-[#777777]"
                  }`}>
                  {v === "timeline" ? "타임라인" : "통계"}
                </button>
              ))}
              {selectedWs && (
                <button type="button" onClick={() => loadHistory(selectedWs)}
                  className="w-6 h-6 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] transition-colors cursor-pointer ml-1">
                  <i className={`ri-refresh-line text-xs ${loadingHistory ? "animate-spin" : ""}`}></i>
                </button>
              )}
            </div>
          </div>

          {/* 통계 뷰 */}
          {historyView === "stats" && (
            <div className="p-4 space-y-3">
              {[
                { label: "총 발송 횟수", value: statsData.total, icon: "ri-send-plane-line", color: "#7A9BFF" },
                { label: "기회 이벤트", value: statsData.opportunity, icon: "ri-star-line", color: "#00C896" },
                { label: "위기 이벤트", value: statsData.crisis, icon: "ri-alert-line", color: "#E8375F" },
                { label: "전체 발송", value: statsData.allBroadcast, icon: "ri-group-line", color: "#FF9F43" },
                { label: "개인 발송", value: statsData.individual, icon: "ri-user-line", color: "#A78BFA" },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between px-3 py-2.5 bg-white/[0.02] rounded-lg border border-white/[0.04]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
                    </div>
                    <span className="text-[#888888] text-xs">{s.label}</span>
                  </div>
                  <span className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</span>
                </div>
              ))}

              {dispatchHistory.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/[0.04]">
                  <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2">최다 발송 이벤트</div>
                  {Object.entries(
                    dispatchHistory.reduce<Record<string, number>>((acc, h) => {
                      acc[h.event] = (acc[h.event] ?? 0) + 1;
                      return acc;
                    }, {})
                  ).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([name, cnt]) => (
                    <div key={name} className="flex items-center justify-between py-1.5">
                      <span className="text-[#555555] text-[10px] truncate max-w-[150px]">{name}</span>
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 rounded-full bg-[#4B6CF7]/40" style={{ width: Math.max(24, cnt * 16) }} />
                        <span className="text-[#3D3D3D] text-[10px] tabular-nums w-4">{cnt}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 타임라인 뷰 */}
          {historyView === "timeline" && (
            <div className="overflow-y-auto" style={{ maxHeight: 520 }}>
              {!selectedWs ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <i className="ri-building-line text-3xl text-[#222222] block mb-3"></i>
                  <p className="text-[#3D3D3D] text-xs">워크스페이스를 선택하면<br />발송 이력이 표시됩니다</p>
                </div>
              ) : loadingHistory ? (
                <div className="flex flex-col gap-3 p-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-3 animate-pulse">
                      <div className="w-8 h-8 rounded-full bg-white/[0.04] shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 bg-white/[0.04] rounded w-3/4" />
                        <div className="h-2.5 bg-white/[0.03] rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : dispatchHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <i className="ri-inbox-line text-3xl text-[#222222] block mb-3"></i>
                  <p className="text-[#3D3D3D] text-xs">아직 발송 이력이 없어요</p>
                  <p className="text-[#2A2A2A] text-[10px] mt-1">이벤트를 발송하면 여기에 기록됩니다</p>
                </div>
              ) : (
                <div className="p-4">
                  {/* 날짜별 그룹핑 */}
                  {(() => {
                    const grouped = dispatchHistory.reduce<Record<string, HistoryRow[]>>((acc, h) => {
                      if (!acc[h.date]) acc[h.date] = [];
                      acc[h.date].push(h);
                      return acc;
                    }, {});

                    return Object.entries(grouped).map(([date, rows]) => (
                      <div key={date} className="mb-5">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-px flex-1 bg-white/[0.04]" />
                          <span className="text-[#3D3D3D] text-[10px] font-semibold px-2">{date}</span>
                          <div className="h-px flex-1 bg-white/[0.04]" />
                        </div>

                        <div className="relative pl-5">
                          {/* 타임라인 세로선 */}
                          <div className="absolute left-1.5 top-2 bottom-2 w-px bg-white/[0.05]" />

                          <div className="space-y-3">
                            {rows.map((h) => (
                              <div key={h.id} className="relative flex gap-3">
                                {/* 타임라인 점 */}
                                <div className={`absolute -left-3.5 top-2 w-2 h-2 rounded-full border shrink-0 ${
                                  h.eventType === "opportunity"
                                    ? "bg-[#4B6CF7] border-[#4B6CF7]/40"
                                    : "bg-[#E8375F] border-[#E8375F]/40"
                                }`} />

                                <div className={`flex-1 rounded-lg border px-3 py-2.5 ${
                                  h.eventType === "opportunity"
                                    ? "bg-[#4B6CF7]/[0.04] border-[#4B6CF7]/15"
                                    : "bg-[#E8375F]/[0.04] border-[#E8375F]/15"
                                }`}>
                                  {/* 이벤트명 + 타겟 */}
                                  <div className="flex items-start justify-between gap-2 mb-1.5">
                                    <span className="text-[#CCCCCC] text-xs font-medium leading-snug flex-1">{h.event}</span>
                                    <span className="text-[9px] text-[#3D3D3D] tabular-nums shrink-0">{h.time}</span>
                                  </div>

                                  <div className="flex items-center gap-2 flex-wrap">
                                    {/* 대상 배지 */}
                                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${
                                      h.toRole === "ALL"
                                        ? "bg-[#FF9F43]/10 text-[#FF9F43] border-[#FF9F43]/25"
                                        : "bg-[#A78BFA]/10 text-[#A78BFA] border-[#A78BFA]/25"
                                    }`}>
                                      {h.toRole === "ALL" ? "전체" : "개인"}
                                    </span>
                                    <span className="text-[#555555] text-[9px] truncate">{h.targetName}</span>

                                    {/* 수치 변화 */}
                                    {(h.capDelta !== 0 || h.repDelta !== 0) && (
                                      <div className="flex items-center gap-1.5 ml-auto">
                                        {h.capDelta !== 0 && (
                                          <span className={`text-[9px] tabular-nums font-medium ${h.capDelta > 0 ? "text-[#7A9BFF]" : "text-[#E8375F]"}`}>
                                            자본 {h.capDelta > 0 ? "+" : ""}{h.capDelta >= 1000 || h.capDelta <= -1000
                                              ? `${(h.capDelta / 1000).toFixed(0)}천만`
                                              : `${h.capDelta}만`}
                                          </span>
                                        )}
                                        {h.repDelta !== 0 && (
                                          <span className={`text-[9px] tabular-nums font-medium ${h.repDelta > 0 ? "text-[#00C896]" : "text-[#E8375F]"}`}>
                                            직급 {h.repDelta > 0 ? "+" : ""}{h.repDelta}pt
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
